import { useEffect, useMemo, useState } from "react";
import { CalendarBlank, CaretLeft, CaretRight, MapPin, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { JOB_STATUS_LABELS, JOB_STATUS_COLORS } from "@/lib/constants";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const iso = (d) => d.toISOString().slice(0, 10);

export default function SiteVisitCalendar() {
  const [items, setItems] = useState(null);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  useEffect(() => { api.get("/jobs/meta/calendar").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  const byDate = useMemo(() => {
    const m = {};
    (items || []).forEach((j) => { (m[j.calendar_date] = m[j.calendar_date] || []).push(j); });
    return m;
  }, [items]);

  if (!items) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading site visit calendar…</div>;

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const monthItems = items.filter((j) => j.calendar_date.startsWith(`${year}-${String(month + 1).padStart(2, "0")}`));
  const conflicts = {};
  monthItems.filter((j) => j.is_visit).forEach((j) => {
    const key = `${j.calendar_date}|${j.field_executive_id || "none"}`;
    conflicts[key] = (conflicts[key] || 0) + 1;
  });
  const hasConflict = (j) => j.is_visit && conflicts[`${j.calendar_date}|${j.field_executive_id || "none"}`] > 1;

  return (
    <div className="space-y-6" data-testid="site-visit-calendar-page">
      <PageHeader
        overline="Valuation Jobs" title="Site Visit Calendar"
        subtitle="Plan field visits across the team — overlaps for the same executive are flagged"
        action={
          <div className="flex items-center gap-2">
            <Button size="icon" variant="outline" className="h-9 w-9 rounded-full" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev"><CaretLeft size={15} /></Button>
            <div className="font-display font-semibold text-primary w-[150px] text-center" data-testid="cal-month">{first.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</div>
            <Button size="icon" variant="outline" className="h-9 w-9 rounded-full" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next"><CaretRight size={15} /></Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-violet-500" /> Scheduled site visit</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500" /> Report deadline</span>
        <span className="inline-flex items-center gap-1.5"><Warning size={13} className="text-rose-500" /> Overlapping visits</span>
      </div>

      <Card className="border-border overflow-hidden">
        <div className="grid grid-cols-7 bg-primary text-white text-[11px] uppercase tracking-wider">
          {DAYS.map((d) => <div key={d} className="px-2 py-2 text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const key = d ? iso(d) : `empty-${i}`;
            const dayItems = d ? (byDate[iso(d)] || []) : [];
            const isToday = d && iso(d) === iso(new Date());
            return (
              <div key={key} className={`min-h-[104px] border-b border-r border-border p-1.5 ${!d ? "bg-muted/40" : isToday ? "bg-accent/[0.07]" : "bg-white"}`} data-testid={d ? `cal-day-${iso(d)}` : undefined}>
                {d && <div className={`text-[11px] font-mono mb-1 ${isToday ? "text-accent font-bold" : "text-muted-foreground"}`}>{d.getDate()}</div>}
                <div className="space-y-1">
                  {dayItems.slice(0, 3).map((j) => (
                    <div key={j.id} title={`${j.applicant_name} · ${j.property_location || j.property_address}`}
                      className={`text-[10px] leading-tight px-1.5 py-1 rounded border truncate ${j.is_visit ? "bg-violet-50 text-violet-900 border-violet-200" : "bg-amber-50 text-amber-900 border-amber-200"}`}
                      data-testid={`cal-item-${j.id}`}>
                      {hasConflict(j) && <Warning size={10} className="inline text-rose-500 mr-0.5" />}
                      {j.applicant_name}
                    </div>
                  ))}
                  {dayItems.length > 3 && <div className="text-[10px] text-muted-foreground pl-1">+{dayItems.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div>
        <div className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-3">Visits & deadlines this month</div>
        {monthItems.length === 0 ? (
          <EmptyState icon={CalendarBlank}>Nothing scheduled this month. Set a site visit date on any job to see it here.</EmptyState>
        ) : (
          <div className="space-y-2">
            {monthItems.map((j) => (
              <Card key={j.id} className="p-3 flex flex-wrap items-center justify-between gap-2 border-border" data-testid={`cal-list-${j.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-primary truncate">{j.applicant_name} · {j.bank_name}</div>
                  <div className="text-xs text-muted-foreground truncate inline-flex items-center gap-1">
                    <MapPin size={12} /> {j.property_location || j.property_address}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${j.is_visit ? "bg-violet-50 text-violet-800 border-violet-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                    {j.is_visit ? "Visit" : "Deadline"} {j.calendar_date}
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${JOB_STATUS_COLORS[j.status]}`}>{JOB_STATUS_LABELS[j.status]}</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
